import {
	useQuoteDispatch,
	useQuoteState,
} from '../../../../stateProviders/QuoteStateProvider';
import { DeckStyle } from './components/DeckStyle';
import { PermitSelection } from './components/PermitSelection';
import Sidebar from '../blocks/components/Sidebar';
import MainCanvas from '../blocks/components/MainCanvas';
import {
	Stack,
	Box,
	Typography,
	FormLabel,
	TextField,
	Select,
	MenuItem,
	IconButton,
	Button,
	FormControlLabel,
	Checkbox,
	styled,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { DeckContext } from '../blocks/context/DeckContext';
import { Edit } from '@mui/icons-material';
import { EditQuotationModal } from '../../modalContent/EditQuotationModal';
import removeBlockRefs from '../../../../utils/removeBlockRefs';
import {
	toastError,
	toastSuccess,
	toastWarning,
} from '../../../../Toaster/Toast.tsx';
import { TbArrowRight } from 'react-icons/tb';

import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'src/hooks/useApi.ts';
import { LastSavedTimestamp } from '../../components/LastSavedTimestamp';
import removeReface from 'src/utils/removeReface.ts';
import { useDesignSpanContext } from '../blocks/context/DesignSpanContext.tsx';
import AppAutocomplete from 'src/globalComponents/AppAutoComplete.tsx';

const StyledLabel = styled(Typography)({
	fontWeight: 400,
	fontSize: 16,
});

export const OptionSelection = () => {
	const quoteState = useQuoteState();
	const quoteDispatch = useQuoteDispatch();
	const { quoteId } = useParams();
	const navigate = useNavigate();
	const api = useApi();
	const {
		state: deckState,
		lastSavedAt,
		isAutoSaving,
		dispatch,
	} = useContext(DeckContext);
	const [editModalOpen, setEditModalOpen] = React.useState(false);

	const [materialTypeOptions, setMaterialTypeOptions] = useState([]);
	const [joistSpacingOptions, setJoistSpacingOptions] = useState([]);

	const { setMaxSpanIn, setMaxCantileverIn } = useDesignSpanContext();

	useEffect(() => {
		(async () => {
			const materialTypeLimiter = await api.limiter.get({
				name: 'Material Type',
			});

			const joistSpacingLimiter = await api.limiter.get({
				name: 'Joist Spacing',
			});

			setMaterialTypeOptions(materialTypeLimiter.rows[0].values);
			setJoistSpacingOptions(joistSpacingLimiter.rows[0].values);
		})();
	}, []);

	useEffect(() => {
		(async () => {
			const materialSpans = await api.utils.getMaterialSpans(
				quoteState.structure
			);
			setMaxSpanIn(materialSpans.maxSpanIn);
			setMaxCantileverIn(materialSpans.maxCantileverIn);
		})();
	}, [quoteState.structure]);

	function validateQuote() {
		let isValid = false;

		isValid = quoteState.options.some(
			(option) =>
				option.deckingType &&
				option.railingType &&
				(option.railingType !== 'IRX' || option.railCapStyle)
		);

		if (!isValid) {
			toastError({
				title: 'Validation Error',
				description: 'At least one option in Step 3 must be fully configured',
			});
		}

		return isValid;
	}

	// Save without validation (for Save button)
	async function saveQuote() {
		if (!quoteId) return;

		const updatedQuote = await api.quotations.updateQuotation(quoteId, {
			blocks: removeBlockRefs(deckState.blocks),
			materialCost: quoteState.materialCost,
			demoSquareFootage: quoteState.demoSquareFootage,
			demoHeight: quoteState.demoHeight,
			selectedPermit: quoteState.selectedPermit,
			engineeringFeeRequired: quoteState.engineeringFeeRequired,
			septicFeeRequired: quoteState.septicFeeRequired,
			miscMaterialCosts: quoteState.miscMaterialCosts,
			miscLaborCosts: quoteState.miscLaborCosts,
			selectedOptionIndex: quoteState.selectedOptionIndex,
			appliedDiscounts: quoteState.appliedDiscounts,
			options: quoteState.options,
			deckboxHeightInFeet: quoteState.deckboxHeightInFeet,
			requiresFascia: quoteState.requiresFascia,
			reface: quoteState.reface,
			structure: quoteState.structure,
			managerDiscount: quoteState.managerDiscount,
		});

		if (!updatedQuote.errored) {
			toastSuccess({
				title: 'Quote Updated',
				description: 'You have successfully updated the quote details.',
			});
		}
		const quote = await api.quotations.getQuotationById(quoteId);
		quoteDispatch({ ...quote, currentQuote: quote });
	}

	// Save with validation (for Save and Get Prices button)
	async function saveQuoteWithValidation() {
		if (!validateQuote()) {
			return false;
		}
		await saveQuote();
		return true;
	}

	return (
		<Box
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{
					flex: 1,
					overflow: 'auto',
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography variant="h1" fontWeight={700}>
						{quoteState?.projectName}
					</Typography>
					<IconButton
						size="small"
						aria-label="Edit Quotation Details"
						onClick={() => setEditModalOpen(true)}
						color="primary"
					>
						<Edit />
					</IconButton>
					<EditQuotationModal
						open={editModalOpen}
						onClose={() => setEditModalOpen(false)}
						quotation={quoteState as any}
						setQuotation={(updatedQuote) => {
							quoteDispatch(updatedQuote);
						}}
					/>
				</Stack>
				<Stack
					direction="row"
					spacing={2}
					sx={{ ml: 'auto', alignItems: 'center' }}
				>
					<LastSavedTimestamp
						lastSavedAt={lastSavedAt}
						isLoading={isAutoSaving}
					/>
					<Button
						variant="outlined"
						color="secondary"
						onClick={async () => {
							await saveQuote();
						}}
					>
						Save
					</Button>
					<Button
						variant="contained"
						color="success"
						endIcon={<TbArrowRight />}
						onClick={async () => {
							const saved = await saveQuoteWithValidation();
							if (!saved) return;

							const errors = [
								...quoteState.drawingErrors,
								...quoteState.optionsErrors,
							];
							if (errors.length > 0) {
								errors.forEach((error) =>
									toastWarning({
										title: 'Validation Warning',
										description: error,
									})
								);
								return;
							}
							navigate(`/quotations/${quoteId}/pricing`);
						}}
					>
						Save and Get Prices
					</Button>
				</Stack>
			</Box>

			{/* Scope Section */}
			<Typography variant="h2" fontWeight={700}>
				Scope
				<Typography
					variant="subtitle1"
					fontWeight={400}
					sx={{ mt: 0.5, fontSize: '14px', color: '#6A7181' }}
				>
					Set the scope of this job
				</Typography>
			</Typography>

			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '1fr', md: '1fr 1fr' }}
				gap={2}
				mt={2}
			>
				<Stack spacing={1} sx={{ minWidth: 180 }}>
					<FormLabel htmlFor="job-type">Job Type</FormLabel>
					<AppAutocomplete
						id="job-type"
						options={['Build or replace deck', 'Reface deck']}
						value={quoteState.reface ? 'Reface deck' : 'Build or replace deck'}
						onChange={(_, value) => {
							quoteDispatch({ reface: value == 'Reface deck' });
							if (value != 'Reface deck') {
								dispatch({
									type: 'UPDATE_BLOCKS',
									payload: removeReface(deckState.blocks),
								});
							}
						}}
						renderInput={(params) => (
							<TextField {...params} variant="outlined" fullWidth />
						)}
					/>
				</Stack>

				<Stack
					spacing={1}
					sx={{
						paddingTop: '20px',
						minWidth: 180,
						justifyContent: 'center',
					}}
				>
					<FormControlLabel
						label={
							<StyledLabel>Include Fascia that Matches Decking</StyledLabel>
						}
						control={
							<Checkbox
								sx={{
									transform: 'scale(1.3)',
									'& .MuiSvgIcon-root': {
										fontSize: 'inherit',
									},
								}}
								checked={quoteState.requiresFascia}
								onChange={() => {
									quoteDispatch({
										requiresFascia: !quoteState.requiresFascia,
									});
								}}
								name="requiresFascia"
								color="primary"
							/>
						}
					/>
				</Stack>
			</Box>

			{/* Demo Costs Section */}
			<Typography variant="h2" fontWeight={700} mt={4}>
				Demo Costs
				<Typography variant="subtitle1" fontWeight={400} sx={{ mt: 0.5 }}>
					What size is the existing deck that needs to be removed?
				</Typography>
			</Typography>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
				gap={2}
				sx={{
					maxWidth: {
						sm: '100%',
						md: '100%',
						lg: '70%',
					},
				}}
			>
				<Stack spacing={1} sx={{ minWidth: 180 }}>
					<FormLabel htmlFor="demoSquareFootage">Square Feet</FormLabel>
					<TextField
						id="demoSquareFootage"
						type="number"
						slotProps={{ htmlInput: { min: 0, step: 1 } }}
						value={quoteState.demoSquareFootage}
						placeholder="Square Feet of Decking"
						variant="outlined"
						fullWidth
						onChange={(e) => {
							const value = e.target.value;
							quoteDispatch({
								demoSquareFootage: value === '' ? '' : parseInt(value),
							});
						}}
					/>
				</Stack>
				<Stack spacing={1} sx={{ minWidth: 180 }}>
					<FormLabel htmlFor="demoHeight">Height (Feet)</FormLabel>
					<TextField
						id="demoHeight"
						type="number"
						slotProps={{ htmlInput: { min: 0, step: 1 } }}
						value={quoteState.demoHeight}
						placeholder="Height of existing deck"
						variant="outlined"
						fullWidth
						onChange={(e) => {
							const value = e.target.value;
							quoteDispatch({
								demoHeight: value === '' ? '' : parseInt(value),
							});
						}}
					/>
				</Stack>
			</Box>

			{/* Logistics Section */}
			<Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4 }}>
				Logistics
			</Typography>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
				gap={2}
				sx={{
					maxWidth: {
						sm: '100%',
						md: '100%',
						lg: '70%',
					},
				}}
			>
				<Stack>
					<FormLabel>Is a permit required?</FormLabel>
					<PermitSelection />
				</Stack>
				<Stack>
					<FormLabel>Does this job require an engineer?</FormLabel>
					<Select
						value={quoteState.engineeringFeeRequired ? '1' : '0'}
						variant="outlined"
						onChange={(e) =>
							quoteDispatch({
								engineeringFeeRequired: e.target.value === '0' ? false : true,
							})
						}
						fullWidth
					>
						<MenuItem value={'0'}>No</MenuItem>
						<MenuItem value={'1'}>Yes</MenuItem>
					</Select>
				</Stack>
				<Stack>
					<FormLabel>Does this job require a septic tank inspection?</FormLabel>
					<Select
						value={quoteState.septicFeeRequired ? '1' : '0'}
						variant="outlined"
						onChange={(e) =>
							quoteDispatch({
								septicFeeRequired: e.target.value === '0' ? false : true,
							})
						}
						fullWidth
					>
						<MenuItem value={'0'}>No</MenuItem>
						<MenuItem value={'1'}>Yes</MenuItem>
					</Select>
				</Stack>
			</Box>

			{/* Deck Options Section */}
			<Typography
				variant="subtitle1"
				sx={{
					mt: 4,
					pb: 0,
					fontStyle: 'semi-bold',
					fontSize: '18px',
					lineHeight: '166%',
					letterSpacing: '0px',
					fontFamily: 'inter',
					fontWeight: 600,
					color: '#001C41',
				}}
			>
				Configure New Deck Options
			</Typography>

			<Typography
				variant="subtitle1"
				fontWeight={600}
				sx={{
					pb: 2,
					fontFamily: 'Inter',
					fontSize: '14px',
					fontStyle: 'normal',
					fontWeight: '400',
					lineHeight: '142%',
					color: '#6A7181',
				}}
			>
				Configure at least one option
			</Typography>
			<DeckStyle />

			{/* Deckbox Section */}
			<Typography
				variant="subtitle1"
				fontWeight={700}
				marginTop={2}
				marginBottom={2}
			>
				Create deckbox, set height, and assign edge types
			</Typography>

			{!quoteState.reface && (
				<Box
					display="grid"
					gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
					gap={2}
					sx={{ marginBottom: '30px' }}
				>
					<Stack spacing={1} sx={{ minWidth: 180 }}>
						<FormLabel htmlFor="demoSquareFootage">Joist Material*</FormLabel>
						<Select
							value={quoteState.structure?.materialType}
							variant="outlined"
							onChange={(e) =>
								quoteDispatch({
									structure: {
										...quoteState.structure,
										materialType: e.target.value,
									},
								})
							}
							fullWidth
						>
							{materialTypeOptions.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</Stack>
					<Stack spacing={1} sx={{ minWidth: 180 }}>
						<FormLabel htmlFor="demoHeight">Joist Spacing*</FormLabel>
						<Select
							value={quoteState.structure?.joistSpacing}
							variant="outlined"
							onChange={(e) =>
								quoteDispatch({
									structure: {
										...quoteState.structure,
										joistSpacing: Number(e.target.value),
									},
								})
							}
							fullWidth
						>
							{joistSpacingOptions.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</Stack>
				</Box>
			)}

			<Box
				display="flex"
				sx={{
					border: '1px solid #ccc',
					borderRadius: 1,
					overflow: 'hidden',
					minHeight: { xs: '300px', md: '400px' },
					height: '400px',
				}}
			>
				<Sidebar />
				<MainCanvas />
			</Box>

			{/* Miscellaneous Costs Section */}
			<Typography variant="subtitle1" fontWeight={700}>
				Add any additional miscellaneous costs that might be necessary for the
				job
			</Typography>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
				gap={2}
				sx={{
					maxWidth: {
						sm: '100%',
						md: '100%',
						lg: '70%',
					},
				}}
			>
				<Stack spacing={1}>
					<FormLabel htmlFor="miscMaterialCosts">Materials ($)</FormLabel>
					<TextField
						id="miscMaterialCosts"
						type="number"
						slotProps={{ htmlInput: { min: 0, step: 1 } }}
						value={quoteState.miscMaterialCosts}
						placeholder="Misc Materials"
						variant="outlined"
						fullWidth
						onChange={(e) => {
							const value = e.target.value;
							quoteDispatch({
								miscMaterialCosts: value === '' ? '' : parseInt(value),
							});
						}}
					/>
				</Stack>
				<Stack spacing={1}>
					<FormLabel htmlFor="miscLaborCosts">Labor ($)</FormLabel>
					<TextField
						id="miscLaborCosts"
						type="number"
						slotProps={{ htmlInput: { min: 0, step: 1 } }}
						value={quoteState.miscLaborCosts}
						placeholder="Misc Labor"
						variant="outlined"
						fullWidth
						onChange={(e) => {
							const value = e.target.value;
							quoteDispatch({
								miscLaborCosts: value === '' ? '' : parseInt(value),
							});
						}}
					/>
				</Stack>
			</Box>
		</Box>
	);
};
